import React, { useState, useEffect } from 'react';
import { UseFormReturn, Controller } from 'react-hook-form';
import { ChevronDown, ChevronRight, HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from '@/components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { SequenceFormData, LORMetadata } from '../types';

interface LORSettingsSectionProps {
  form: UseFormReturn<SequenceFormData>;
  isExpanded: boolean;
  onToggleExpanded: () => void;
}

const LORSettingsSection: React.FC<LORSettingsSectionProps> = ({
  form,
  isExpanded,
  onToggleExpanded,
}) => {
  const [customPropType, setCustomPropType] = useState('');
  const [showCustomPropInput, setShowCustomPropInput] = useState(false);

  // Session storage persistence for section state
  useEffect(() => {
    const savedState = sessionStorage.getItem('lor-settings-expanded');
    if (savedState === 'true' && !isExpanded) {
      onToggleExpanded();
    }
  }, []);

  const handleToggleExpanded = () => {
    const newState = !isExpanded;
    sessionStorage.setItem('lor-settings-expanded', newState.toString());
    onToggleExpanded();
  };

  const watchControllerType = form.watch('lor_metadata.controller_type');
  const watchPropTypes = form.watch('lor_metadata.prop_types') || [];

  const predefinedPropTypes = [
    'Singing Faces',
    'Mega Tree',
    'CCR/Pixel Tree',
    'Matrix',
    'Arches',
    'Floods',
  ];

  const handlePropTypeToggle = (propType: string, checked: boolean) => {
    const currentPropTypes = form.getValues('lor_metadata.prop_types') || [];
    
    if (checked) {
      form.setValue('lor_metadata.prop_types', [...currentPropTypes, propType]);
    } else {
      form.setValue('lor_metadata.prop_types', currentPropTypes.filter(pt => pt !== propType));
    }
  };

            const handleCustomPropTypeAdd = () => {
            if (customPropType.trim()) {
              const currentPropTypes = form.getValues('lor_metadata.prop_types') || [];
              
              // Check if prop type already exists
              if (currentPropTypes.includes(customPropType.trim())) {
                toast.error('This prop type already exists');
                return;
              }
              
              // Check length limit
              if (customPropType.trim().length > 100) {
                toast.error('Prop type must be 100 characters or less');
                return;
              }
              
              // Check total prop types limit
              if (currentPropTypes.length >= 20) {
                toast.error('Maximum 20 prop types allowed');
                return;
              }
              
              form.setValue('lor_metadata.prop_types', [...currentPropTypes, customPropType.trim()]);
              setCustomPropType('');
              setShowCustomPropInput(false);
            }
          };

  const handleCustomPropTypeRemove = (propType: string) => {
    const currentPropTypes = form.getValues('lor_metadata.prop_types') || [];
    form.setValue('lor_metadata.prop_types', currentPropTypes.filter(pt => pt !== propType));
  };

  const handleControllerTypeChange = (newType: string) => {
    const currentType = form.getValues('lor_metadata.controller_type');
    const currentChannelCount = form.getValues('lor_metadata.channel_count_ac');
    
    // If switching away from AC_ONLY or MIXED and channel count is populated, confirm
    if ((currentType === 'AC_ONLY' || currentType === 'MIXED') && 
        (newType === 'RGB') && 
        currentChannelCount) {
      if (confirm('Switching to RGB controller type will clear the channel count. Continue?')) {
        form.setValue('lor_metadata.channel_count_ac', undefined);
        form.setValue('lor_metadata.controller_type', newType as any);
      }
    } else {
      form.setValue('lor_metadata.controller_type', newType as any);
    }
  };

  return (
    <Card className="w-full">
      <CardHeader 
        className="cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={handleToggleExpanded}
      >
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <span>Light-O-Rama Settings</span>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <HelpCircle className="h-4 w-4 text-gray-500" />
                </TooltipTrigger>
                <TooltipContent>
                  <p>Configure Light-O-Rama specific settings for your sequence</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </CardTitle>
          {isExpanded ? (
            <ChevronDown className="h-5 w-5 text-gray-500" />
          ) : (
            <ChevronRight className="h-5 w-5 text-gray-500" />
          )}
        </div>
      </CardHeader>

      {isExpanded && (
        <CardContent className="space-y-6">
          {/* Controller Type */}
          <FormField
            control={form.control}
            name="lor_metadata.controller_type"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Controller Type *</FormLabel>
                <FormControl>
                  <Select onValueChange={handleControllerTypeChange} defaultValue={field.value}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select controller type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="AC_ONLY">AC Only (traditional CTB16/AC controllers)</SelectItem>
                      <SelectItem value="RGB">RGB (Pixel only; Pixie/PixCon/etc.)</SelectItem>
                      <SelectItem value="MIXED">Mixed (AC + RGB)</SelectItem>
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Channel Count (AC) - Conditional */}
          {(watchControllerType === 'AC_ONLY' || watchControllerType === 'MIXED') && (
            <FormField
              control={form.control}
              name="lor_metadata.channel_count_ac"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Channel Count (AC)</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="number"
                      min="1"
                      max="512"
                      placeholder="Enter channel count (1-512)"
                      value={field.value || ''}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                    />
                  </FormControl>
                  <FormDescription>
                    Exact match helps buyers filter by their controller setup.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {/* Software Version */}
          <FormField
            control={form.control}
            name="lor_metadata.software_version"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Software Version</FormLabel>
                <FormControl>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select software version" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="S4">S4</SelectItem>
                      <SelectItem value="S5">S5</SelectItem>
                      <SelectItem value="S6">S6</SelectItem>
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormDescription>
                  LOR sequences are not always backward-compatible.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

                            {/* Prop Types */}
                  <FormField
                    control={form.control}
                    name="lor_metadata.prop_types"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center justify-between">
                          <span>Prop Types</span>
                          <span className="text-sm text-gray-500">
                            {watchPropTypes.length}/20
                          </span>
                        </FormLabel>
                        <FormControl>
                          <div className="space-y-3">
                            {/* Predefined prop types */}
                            <div className="grid grid-cols-2 gap-3">
                              {predefinedPropTypes.map((propType) => (
                                <div key={propType} className="flex items-center space-x-2">
                                  <Checkbox
                                    id={`prop-${propType}`}
                                    checked={watchPropTypes.includes(propType)}
                                    onCheckedChange={(checked) =>
                                      handlePropTypeToggle(propType, checked as boolean)
                                    }
                                    disabled={!watchPropTypes.includes(propType) && watchPropTypes.length >= 20}
                                  />
                                  <label
                                    htmlFor={`prop-${propType}`}
                                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                  >
                                    {propType}
                                  </label>
                                </div>
                              ))}
                            </div>

                                                {/* Custom prop type */}
                            <div className="space-y-2">
                              <div className="flex items-center space-x-2">
                                <Checkbox
                                  id="prop-custom"
                                  checked={showCustomPropInput}
                                  onCheckedChange={(checked) => setShowCustomPropInput(checked as boolean)}
                                  disabled={watchPropTypes.length >= 20}
                                />
                                <label
                                  htmlFor="prop-custom"
                                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                >
                                  Custom Prop
                                </label>
                              </div>

                              {showCustomPropInput && (
                                <div className="flex gap-2">
                                  <Input
                                    placeholder="Enter custom prop type"
                                    value={customPropType}
                                    onChange={(e) => setCustomPropType(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && handleCustomPropTypeAdd()}
                                    disabled={watchPropTypes.length >= 20}
                                  />
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={handleCustomPropTypeAdd}
                                    disabled={watchPropTypes.length >= 20}
                                  >
                                    Add
                                  </Button>
                                </div>
                              )}
                            </div>

                    {/* Selected prop types display */}
                    {watchPropTypes.length > 0 && (
                      <div className="flex flex-wrap gap-2 pt-2">
                        {watchPropTypes.map((propType) => (
                          <Badge key={propType} variant="secondary" className="flex items-center gap-1">
                            {propType}
                            <button
                              type="button"
                              onClick={() => handleCustomPropTypeRemove(propType)}
                              className="ml-1 text-gray-500 hover:text-gray-700"
                            >
                              ×
                            </button>
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* License Level Required */}
          <FormField
            control={form.control}
            name="lor_metadata.license_level_required"
            render={({ field }) => (
              <FormItem>
                <FormLabel>License Level Required</FormLabel>
                <FormControl>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select license level" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="BASIC">Basic</SelectItem>
                      <SelectItem value="STANDARD">Standard</SelectItem>
                      <SelectItem value="ADVANCED">Advanced</SelectItem>
                      <SelectItem value="PRO">Pro</SelectItem>
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormDescription>
                  Minimum LOR license needed to run this sequence.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Sequence Type Tags */}
          <FormField
            control={form.control}
            name="lor_metadata.sequence_type_tags"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Sequence Type Tags</FormLabel>
                <FormControl>
                  <div className="grid grid-cols-3 gap-3">
                    {(['TRADITIONAL', 'RGB_PLUS', 'SINGING_FACES'] as const).map((tag) => (
                      <div key={tag} className="flex items-center space-x-2">
                        <Checkbox
                          id={`tag-${tag}`}
                          checked={field.value?.includes(tag) || false}
                          onCheckedChange={(checked) => {
                            const currentTags = field.value || [];
                            if (checked) {
                              field.onChange([...currentTags, tag]);
                            } else {
                              field.onChange(currentTags.filter(t => t !== tag));
                            }
                          }}
                        />
                        <label
                          htmlFor={`tag-${tag}`}
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                        >
                          {tag.replace('_', ' ')}
                        </label>
                      </div>
                    ))}
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

                            {/* Notes for Import */}
                  <FormField
                    control={form.control}
                    name="lor_metadata.import_notes"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Notes for Import</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Textarea
                              {...field}
                              placeholder="e.g., 'Uses 4-face quartet,' 'Timing grid requires Tapper,' 'Uses S5 motion effect rows'"
                              className="min-h-20"
                              maxLength={2000}
                            />
                            <div className="absolute bottom-2 right-2 text-xs text-gray-500">
                              {(field.value?.length || 0)}/2000
                            </div>
                          </div>
                        </FormControl>
                        <FormDescription>
                          Additional notes to help users import and use your sequence.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
        </CardContent>
      )}
    </Card>
  );
};

export default LORSettingsSection;
